"use client";

import { useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useCustomerId } from "@/contexts/customer-id-context";
import { useIsFeatureActive } from "@/hooks/use-is-feature-active";
import { useEntitySubscription } from "@/hooks/subscriptions/use-entity-subscription";
import {
  DASHBOARD_FIELDS,
  parseDashboard,
  type DashboardResponse,
} from "@/services/dashboards.service";
import type { EntityEventKind } from "@/services/subscription.service";
import type { Dashboard } from "@/types/dashboards";
import { dashboardsQueryKey } from "./use-dashboards";

/**
 * Keeps the cached dashboard list (`dashboardsQueryKey`) live: registers a single
 * `Dashboard` subscription on the shared websocket and patches the React Query
 * cache in place for every create/update/delete the backend delivers — no
 * refetch. This mirrors `useEntityInstanceList`'s cache-patching, scoped to the
 * dashboard list.
 *
 * The subscription projects the same `DASHBOARD_FIELDS` the list query fetches,
 * so an event payload parses into a full `Dashboard` and replaces all fields of
 * the matching row.
 *
 * Gated behind the opt-in Dashboards feature so we never open a subscription for
 * a tenant that has no `Dashboard` entity. Intended to be mounted once (from the
 * sidebar folder, which itself only renders when the feature is active).
 */
export function useDashboardsSubscription() {
  const customerId = useCustomerId();
  const queryClient = useQueryClient();
  const dashboardsEnabled = useIsFeatureActive("Dashboard");

  const onEvent = useCallback(
    (kind: EntityEventKind, data: Record<string, unknown>) => {
      const id = data?.id as string | undefined;
      if (!id) return;

      const queryKey = dashboardsQueryKey(customerId);

      queryClient.setQueryData<Dashboard[]>(queryKey, (prev) => {
        if (kind === "delete") {
          if (!prev) return prev;
          return prev.filter((d) => d.id !== id);
        }

        // create or update: parse the full payload and slot it in by id. If the
        // row already exists (e.g. the echo of our own optimistic write, or an
        // update), replace all its fields; otherwise it's a new dashboard.
        const dashboard = parseDashboard(data as unknown as DashboardResponse);
        if (!prev) return [dashboard];

        const existing = prev.find((d) => d.id === id);
        // `parseDashboard` hands back the very same object for a row that hasn't
        // changed at all; returning `prev` then keeps React Query from notifying
        // anyone. It also preserves the `widgets` array across a write that only
        // touched `cached_results`, which is what stops a cache persist echoing
        // back as a re-render of every chart on the dashboard.
        if (existing === dashboard) return prev;
        if (existing) return prev.map((d) => (d.id === id ? dashboard : d));

        if (kind === "update") return prev; // updated dashboard not in the list — nothing to do
        return [...prev, dashboard];
      });
    },
    [queryClient, customerId],
  );

  useEntitySubscription({
    entityName: "Dashboard",
    fieldsSelection: DASHBOARD_FIELDS,
    onEvent,
    enabled: dashboardsEnabled && customerId !== null,
  });
}
